// One visitor's conversation with one talking NPC, shared by the desktop chat
// (NpcTalkPanel) and the VR panel (NpcTalkVR): the lines so far, the greeting,
// typed or spoken turns, and the NPC's voice (louder the closer you stand).
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNpcTalk } from './npcTalkStore';
import { talkToNpc, listenToVisitor, npcCanTalk, TalkError, type ChatLine } from '../../services/npcTalk';

export type Line = ChatLine | { role: 'note'; content: string };
export type Busy = '' | 'thinking' | 'listening' | 'recording';

/** Longest spoken turn; recording stops and sends on its own after this. */
const MAX_CLIP_MS = 15_000;

function pickRecorderType(): string {
  for (const t of ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4']) {
    if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported?.(t)) return t;
  }
  return '';
}

const volumeAt = (dist: number) => Math.max(0.15, Math.min(1, 1.6 / Math.max(1, dist)));

/** Is the owner able to pay for this NPC? Checked once a minute, only while you're next to it. */
const canTalkCache = new Map<string, { ok: boolean; at: number }>();
export function useNpcCanTalk(worldId: string | undefined, npcId: string | null): boolean {
  const key = worldId && npcId ? `${worldId}:${npcId}` : '';
  const [, bump] = useState(0);
  useEffect(() => {
    if (!key) return;
    const c = canTalkCache.get(key);
    if (c && Date.now() - c.at < 60_000) return;
    let live = true;
    npcCanTalk(worldId!, npcId!).then((ok) => { canTalkCache.set(key, { ok, at: Date.now() }); if (live) bump((n) => n + 1); });
    return () => { live = false; };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  return !!(key && canTalkCache.get(key)?.ok);
}

export function useNpcConversation(worldId: string, npcId: string, hasGreeting: boolean, onIdle?: () => void) {
  const [lines, setLines] = useState<Line[]>([]);
  const [busy, setBusy] = useState<Busy>('');
  const [resting, setResting] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const recRef = useRef<{ rec: MediaRecorder; chunks: Blob[]; stream: MediaStream; timer: number } | null>(null);
  const linesRef = useRef<Line[]>([]);
  linesRef.current = lines;
  const wantRec = useRef(false);
  const idle = useRef(onIdle);
  idle.current = onIdle;

  // Voice volume follows distance while it plays.
  useEffect(() => useNpcTalk.subscribe((s) => {
    const a = audioRef.current;
    if (a) a.volume = volumeAt(s.nearDist);
  }), []);
  useEffect(() => () => {
    audioRef.current?.pause();
    const r = recRef.current;
    if (r) { clearTimeout(r.timer); r.stream.getTracks().forEach((t) => t.stop()); }
    useNpcTalk.getState().setSpeaking(null);
  }, []);

  const speak = useCallback((b64: string | null) => {
    audioRef.current?.pause();
    if (!b64) return;
    const a = new Audio(`data:audio/mpeg;base64,${b64}`);
    a.volume = volumeAt(useNpcTalk.getState().nearDist);
    audioRef.current = a;
    useNpcTalk.getState().setSpeaking(npcId);
    a.onended = () => useNpcTalk.getState().setSpeaking(null);
    a.play().catch(() => useNpcTalk.getState().setSpeaking(null));
  }, [npcId]);

  const send = useCallback(async (said: string) => {
    const msg = said.trim();
    if (!msg || resting) return;
    const next: Line[] = [...linesRef.current, { role: 'user', content: msg }];
    setLines(next);
    setBusy('thinking');
    try {
      const r = await talkToNpc(worldId, npcId, next.filter((l): l is ChatLine => l.role !== 'note'));
      if (r.resting) setResting(true);
      if (r.reply) setLines((ls) => [...ls, { role: 'assistant', content: r.reply! }]);
      speak(r.audio);
    } catch (err) {
      setLines((ls) => [...ls, { role: 'note', content: err instanceof TalkError ? err.message : 'Something went wrong. Try again.' }]);
    } finally {
      setBusy('');
      idle.current?.();
    }
  }, [worldId, npcId, resting, speak]);

  // Greeting when the conversation opens.
  useEffect(() => {
    if (!hasGreeting) { idle.current?.(); return; }
    let live = true;
    setBusy('thinking');
    talkToNpc(worldId, npcId, [], true)
      .then((r) => {
        if (!live) return;
        if (r.resting) setResting(true);
        if (r.reply) setLines([{ role: 'assistant', content: r.reply }]);
        speak(r.audio);
      })
      .catch(() => { /* no greeting; they can still talk */ })
      .finally(() => { if (live) { setBusy(''); idle.current?.(); } });
    return () => { live = false; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const stopRec = useCallback(async () => {
    wantRec.current = false;
    const r = recRef.current;
    if (!r) return;
    recRef.current = null;
    clearTimeout(r.timer);
    const done = new Promise<void>((res) => { r.rec.onstop = () => res(); });
    r.rec.stop();
    await done;
    r.stream.getTracks().forEach((t) => t.stop());
    const clip = new Blob(r.chunks, { type: r.rec.mimeType || 'audio/webm' });
    if (clip.size < 2000) { setBusy(''); return; } // a tap, not speech
    setBusy('listening');
    try {
      const heard = await listenToVisitor(worldId, npcId, clip);
      setBusy('');
      if (heard) void send(heard);
    } catch (err) {
      setBusy('');
      setLines((ls) => [...ls, { role: 'note', content: err instanceof TalkError ? err.message : "Couldn't hear that." }]);
    }
  }, [worldId, npcId, send]);

  const startRec = useCallback(async () => {
    if (busy || resting || recRef.current) return;
    wantRec.current = true;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Let go before the mic opened (or the permission prompt): don't record.
      if (!wantRec.current) { stream.getTracks().forEach((t) => t.stop()); return; }
      const type = pickRecorderType();
      const rec = new MediaRecorder(stream, type ? { mimeType: type } : undefined);
      const chunks: Blob[] = [];
      rec.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data); };
      rec.start();
      audioRef.current?.pause(); // don't talk over the NPC, and don't record it
      useNpcTalk.getState().setSpeaking(null);
      recRef.current = { rec, chunks, stream, timer: window.setTimeout(() => void stopRec(), MAX_CLIP_MS) };
      setBusy('recording');
    } catch {
      wantRec.current = false;
      setLines((ls) => [...ls, { role: 'note', content: 'Microphone not available. You can type instead.' }]);
    }
  }, [busy, resting, stopRec]);

  return { lines, busy, resting, send, startRec, stopRec, recording: busy === 'recording' };
}
